import { RippleButton } from "./ripple-button";

import { cn } from "@/lib/utils";

// MUI's `CardActionArea`, as the donor's gallery uses it — `ImagePreview.tsx:29`
// wraps **both** of its branches in one, the picture and the no-image placeholder
// alike.
//
// The feedback, transcribed from `CardActionArea.js:43-71` and
// `createPalette.js:47,57`: a `currentcolor` overlay at `action.hoverOpacity`
// 0.04, `action.focusOpacity` 0.12 on focus-visible, over a 250ms opacity
// transition, plus `ButtonBase`'s touch ripple.
//
// Two details that are easy to get wrong and both visible:
//
//   - **Square corners.** The donor's overlay is `border-radius: inherit`
//     (`CardActionArea.js:39`), which resolves to 0 against a parent with none —
//     so it overhangs a 4px-rounded picture by a hair. Faithful, and free.
//   - **No focus outline.** `ButtonBase` sets `outline: 0`; the 0.12 tint *is*
//     the focus indicator. Without this the UA ring paints around the whole
//     full-width picture on top of the tint.
//
// Its own component because the gallery has two of them — the picture slot and
// the placeholder slot — and the second one is a *dead* affordance: the donor's
// `onClick` sits on the inner `CardMedia` (`:44`), which the placeholder branch
// does not render, so activating it does nothing. **Ruled kept** by the owner on
// #303's decision round (D1), so the dead affordance is the shipped behaviour
// and not an oversight.

interface ItemActionAreaProps {
  children: React.ReactNode;
  /** Absent for the donor's dead affordance — the area still draws its feedback. */
  onClick?: () => void;
  /**
   * The control's accessible name, where its contents do not give it a useful one.
   *
   * MUI's `CardActionArea` carries no label and computes its name from what is
   * inside it, which on the gallery's main slot is the picture's `alt` — the bare
   * item name, saying nothing about what activating it does. Naming it is an
   * **owner-ruled parity break** (#303, D2).
   */
  ariaLabel?: string;
  className?: string;
  "data-testid"?: string;
}

export function ItemActionArea({
  children,
  onClick,
  ariaLabel,
  className,
  "data-testid": testId,
}: ItemActionAreaProps) {
  return (
    // No `focusRipple`: the donor is a `CardActionArea`, which never sets it,
    // so Space activates the card without inking.
    <RippleButton
      data-testid={testId}
      aria-label={ariaLabel}
      onClick={onClick}
      className={cn(
        // `relative` for the ripple layer and the hover overlay, both
        // `absolute inset-0`. `overflow-hidden` is for the picture inside, not
        // for the ink — the ripple layer clips itself.
        "relative overflow-hidden",
        "group block w-full cursor-pointer text-left focus-visible:outline-none",
        className,
      )}
    >
      {children}
      <span
        aria-hidden="true"
        className={cn(
          "pointer-events-none absolute inset-0 bg-current opacity-0",
          "transition-opacity duration-[250ms] ease-in-out",
          "group-hover:opacity-[0.04] group-focus-visible:opacity-[0.12]",
        )}
      />
    </RippleButton>
  );
}
